import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { X, Camera } from 'lucide-react';

const QRAttendanceModal = ({ isOpen, onClose, onScanSuccess, actionLabel }) => {
    const scannerRef = useRef(null);
    const startPromiseRef = useRef(null);
    const onScanSuccessRef = useRef(onScanSuccess);
    const [scannerError, setScannerError] = useState(null);

    useEffect(() => {
        onScanSuccessRef.current = onScanSuccess;
    }, [onScanSuccess]);

    const [hasOpened, setHasOpened] = useState(false);
    useEffect(() => { if (isOpen) setHasOpened(true); }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        let isUnmounted = false;
        const scannerId = "reader";
        let localScanner = null;
        let localStartPromise = null;
        
        // Ensure element exists before initializing
        const initScanner = () => {
            if (!document.getElementById(scannerId)) return;
            if (isUnmounted) return;
            
            try {
                localScanner = new Html5Qrcode(scannerId);
                scannerRef.current = localScanner;

                localStartPromise = localScanner.start(
                    { facingMode: "environment" },
                    {
                        fps: 10,
                        qrbox: { width: 250, height: 250 },
                        aspectRatio: 1.0,
                        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE]
                    },
                    (decodedText) => {
                        onScanSuccessRef.current(decodedText);
                    },
                    (errorMessage) => {}
                );
                
                startPromiseRef.current = localStartPromise;
                
                localStartPromise.catch(err => {
                    if (isUnmounted) return;
                    if (String(err).includes("play()")) return; // Ignore harmless interrupted error
                    console.error("Scanner start error:", err);
                    setScannerError("Failed to access camera. Please allow camera permissions.");
                });
            } catch (err) {
                if (isUnmounted) return;
                console.error("Scanner init error:", err);
                setScannerError("Failed to initialize camera. Please check permissions.");
            }
        };

        const timeoutId = setTimeout(initScanner, 100);

        return () => {
            isUnmounted = true;
            clearTimeout(timeoutId);
            
            if (localScanner) {
                const stopScanner = () => {
                    try {
                        if (localScanner.isScanning) {
                            localScanner.stop().then(() => {
                                try { localScanner.clear(); } catch(e) {}
                            }).catch(() => {
                                try { localScanner.clear(); } catch(e) {}
                            });
                        } else {
                            try { localScanner.clear(); } catch(e) {}
                        }
                    } catch (e) {
                        try { localScanner.clear(); } catch(err) {}
                    }
                };

                if (localStartPromise) {
                    localStartPromise.then(stopScanner).catch(stopScanner);
                } else {
                    stopScanner();
                }
            }
        };
    }, [isOpen]);

    if (!hasOpened) return null;

    return (
        <div className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-all duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl p-6 overflow-hidden animate-fade-in border border-neutral-200 dark:border-neutral-800">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-500 transition-colors z-10"
                >
                    <X size={20} />
                </button>

                <div className="text-center mb-6 mt-2">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] mb-4">
                        <Camera size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-neutral-900 dark:text-white">Scan QR Code</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
                        Point your camera at the office QR code to confirm {actionLabel?.toLowerCase() || 'attendance'}.
                    </p>
                </div>

                {scannerError ? (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-center text-sm font-medium">
                        {scannerError}
                    </div>
                ) : (
                    <div className="rounded-xl overflow-hidden bg-black aspect-square relative border-2 border-[var(--accent-primary)]/30">
                        <div id="reader" className="w-full h-full"></div>
                    </div>
                )}
            </div>
            
            {/* Some CSS overrides for the ugly html5-qrcode UI */}
            <style dangerouslySetInnerHTML={{__html: `
                #reader { border: none !important; }
                #reader video { object-fit: cover; border-radius: 12px; }
            `}} />
        </div>
    );
};

export default QRAttendanceModal;
